import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import { systemUserTypes } from "./settingsConfig";

const emptyUserForm = {
  id: null,
  first_name: "",
  middle_name: "",
  last_name: "",
  email: "",
  username: "",
  phone: "",
  user_type: "ADMIN",
  role: "",
  status: "ACTIVE",
  is_active: true,
  password: "",
};

function SystemUsers({ users, roles, onReload }) {
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [createdUser, setCreatedUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  const filteredUsers = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    if (!term) return users;

    return users.filter((user) => {
      return (
        user.full_name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.username?.toLowerCase().includes(term) ||
        user.role_name?.toLowerCase().includes(term)
      );
    });
  }, [users, userSearch]);

  const getStatusLabel = (user) => {
    if (!user.is_active) return "INACTIVE";
    return user.status || "ACTIVE";
  };

  const getStatusBadgeClass = (user) => {
    if (!user.is_active) return "text-bg-secondary";
    if (user.status === "SUSPENDED") return "text-bg-danger";
    if (user.status === "PENDING") return "text-bg-warning";
    return "text-bg-success";
  };

  const handleUserChange = (event) => {
    const { name, value, type, checked } = event.target;

    setUserForm((prev) => {
      const nextForm = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "is_active" && !checked) {
        nextForm.status = "INACTIVE";
      }

      if (name === "is_active" && checked && prev.status === "INACTIVE") {
        nextForm.status = "ACTIVE";
      }

      if (name === "status") {
        nextForm.is_active = value !== "INACTIVE";
      }

      return nextForm;
    });
  };

  const selectUser = (user) => {
    setCreatedUser(null);

    setUserForm({
      id: user.id,
      first_name: user.first_name || "",
      middle_name: user.middle_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      username: user.username || "",
      phone: user.phone || "",
      user_type: user.user_type || "ADMIN",
      role: user.role || "",
      status: user.status || "ACTIVE",
      is_active: Boolean(user.is_active),
      password: "",
    });
  };

  const resetUserForm = () => {
    setUserForm(emptyUserForm);
    setCreatedUser(null);
  };

  const saveUser = async (event) => {
    event.preventDefault();
    setCreatedUser(null);

    if (
      !userForm.first_name.trim() ||
      !userForm.last_name.trim() ||
      !userForm.email.trim()
    ) {
      toast.warning("First name, last name and email are required.");
      return;
    }

    try {
      setSavingUser(true);

      const payload = {
        ...userForm,
        role: userForm.role || null,
        status:
          userForm.is_active && userForm.status === "INACTIVE"
            ? "ACTIVE"
            : userForm.is_active
            ? userForm.status
            : "INACTIVE",
        is_active: userForm.status !== "INACTIVE" && userForm.is_active,
      };

      if (!payload.password) {
        delete payload.password;
      }

      let response;

      if (userForm.id) {
        response = await axiosInstance.put(
          `settings/system-users/${userForm.id}/`,
          payload
        );
        toast.success("System user updated successfully.");
      } else {
        response = await axiosInstance.post("settings/system-users/", payload);
        toast.success("System user created successfully.");
        setCreatedUser(response.data);
      }

      resetUserForm();

      if (!userForm.id) {
        setCreatedUser(response.data);
      }

      onReload();
    } catch (error) {
      console.error(error);

      const data = error.response?.data;
      const message =
        data?.detail ||
        Object.values(data || {})?.flat?.()?.[0] ||
        "Failed to save system user.";

      toast.error(message);
    } finally {
      setSavingUser(false);
    }
  };

  const copyUserCredentials = async () => {
    if (!createdUser) return;

    const text = `Name: ${createdUser.full_name}
Email: ${createdUser.email}
Username: ${createdUser.username || createdUser.email}
Temporary Password: ${createdUser.temporary_password}`;

    try {
      await navigator.clipboard.writeText(text);
      toast.success("Login details copied.");
    } catch (error) {
      toast.info("Copy failed. Highlight and copy the details manually.");
    }
  };

  return (
    <div className="row g-4">
      <div className="col-xl-5">
        {createdUser && (
          <div className="alert alert-success border-0 shadow-sm">
            <h6 className="fw-bold">System user created</h6>
            <div>
              <strong>Name:</strong> {createdUser.full_name}
            </div>
            <div>
              <strong>Email:</strong> {createdUser.email}
            </div>
            <div>
              <strong>Password:</strong> {createdUser.temporary_password}
            </div>

            <button
              className="btn btn-success btn-sm mt-3"
              onClick={copyUserCredentials}
            >
              <i className="bi bi-clipboard-check me-2"></i>
              Copy Login Details
            </button>
          </div>
        )}

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <h5 className="mb-0">
              {userForm.id ? "Edit System User" : "Add System User"}
            </h5>
          </div>

          <div className="card-body">
            <form onSubmit={saveUser}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">First Name</label>
                  <input
                    name="first_name"
                    className="form-control"
                    value={userForm.first_name}
                    onChange={handleUserChange}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Last Name</label>
                  <input
                    name="last_name"
                    className="form-control"
                    value={userForm.last_name}
                    onChange={handleUserChange}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={userForm.email}
                    onChange={handleUserChange}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Username</label>
                  <input
                    name="username"
                    className="form-control"
                    value={userForm.username}
                    onChange={handleUserChange}
                    placeholder="Optional"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Phone</label>
                  <input
                    name="phone"
                    className="form-control"
                    value={userForm.phone}
                    onChange={handleUserChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">User Type</label>
                  <select
                    name="user_type"
                    className="form-select"
                    value={userForm.user_type}
                    onChange={handleUserChange}
                  >
                    {systemUserTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Role</label>
                  <select
                    name="role"
                    className="form-select"
                    value={userForm.role || ""}
                    onChange={handleUserChange}
                  >
                    <option value="">No role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    className="form-select"
                    value={userForm.status}
                    onChange={handleUserChange}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="PENDING">Pending</option>
                    <option value="SUSPENDED">Suspended</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label">
                    Password{" "}
                    <span className="text-muted">
                      (leave blank to auto-generate)
                    </span>
                  </label>
                  <input
                    name="password"
                    className="form-control"
                    value={userForm.password}
                    onChange={handleUserChange}
                  />
                </div>

                <div className="col-12">
                  <label className="form-check">
                    <input
                      type="checkbox"
                      name="is_active"
                      className="form-check-input"
                      checked={userForm.is_active}
                      onChange={handleUserChange}
                    />
                    <span className="form-check-label">User account is active</span>
                  </label>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={resetUserForm}
                >
                  Clear
                </button>

                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={savingUser}
                >
                  {savingUser ? "Saving..." : userForm.id ? "Update User" : "Add User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="col-xl-7">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <h5 className="mb-0">Internal System Users</h5>

              <input
                type="search"
                className="form-control"
                style={{ maxWidth: 280 }}
                placeholder="Search users..."
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
              />
            </div>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>User</th>
                    <th>Type</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="fw-bold">{user.full_name}</div>
                        <small className="text-muted">{user.email}</small>
                      </td>

                      <td>{user.user_type}</td>
                      <td>{user.role_name || "No role"}</td>

                      <td>
                        <span className={`badge ${getStatusBadgeClass(user)}`}>
                          {getStatusLabel(user)}
                        </span>
                      </td>

                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-light border"
                          onClick={() => selectUser(user)}
                          title="Edit user"
                        >
                          <i className="bi bi-pencil-square text-success"></i>
                        </button>
                      </td>
                    </tr>
                  ))}

                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        No system users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SystemUsers;
